import { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { readStoredAuth } from '../api/authApi';
import { contextoAsistencia } from '../services/asistenciaOfflineService';
import { JORNADA_CHANGED, leerJornadasLocales, iniciarPausaJornada, finalizarPausaJornada } from '../services/jornadaOfflineService';
import { useAuth } from '../auth/AuthContext';
import { canRrhh } from '../services/rrhhAccess';

export default function JornadaPausasControl({ shift }) {
    const { user } = useAuth();
    const allowedStart = canRrhh(user, 'asistencias.registrar_pausas');
    const allowedEnd = canRrhh(user, 'asistencias.finalizar_pausas');
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!shift.active || shift.schemaVersion !== 2 || (!allowedStart && !allowedEnd)) return undefined;
        let disposed = false;
        const reload = async () => {
            try {
                const session = await readStoredAuth();
                if (!session?.user?.id) return;
                const [rows, context] = await Promise.all([leerJornadasLocales(session.user.id), contextoAsistencia({ refresh: false })]);
                if (!disposed) setData({ row: rows.find((r) => r.id === shift.jornadaId), context, userId: session.user.id });
            } catch (error) { if (!disposed) setMessage(error.message); }
        };
        void reload();
        void contextoAsistencia().then(() => { if (!disposed) return reload(); })
            .catch((error) => { if (!disposed) setMessage(error.message || 'No se pudieron actualizar las pausas.'); });
        window.addEventListener(JORNADA_CHANGED, reload);
        const timer = setInterval(() => setNow(Date.now()), 30000);
        return () => { disposed = true; clearInterval(timer); window.removeEventListener(JORNADA_CHANGED, reload); };
    }, [shift.jornadaId, shift.active, shift.schemaVersion, allowedStart, allowedEnd]);
    if (!shift.active || shift.schemaVersion !== 2) return null;
    const types = data?.context?.tipos_pausa || [];
    const pauses = data?.row?.pausas || [];
    const active = pauses.find((p) => !p.fin);
    const canStart = allowedStart && data?.context?.permissions?.registrar_pausas;
    const canEnd = allowedEnd && data?.context?.permissions?.finalizar_pausas;
    if (!canStart && !active) return null;
    const run = async (tipo) => {
        if (busy) return;
        if (active ? !canEnd : !canStart) return;
        setBusy(true); setMessage(null);
        try {
            if (active) await finalizarPausaJornada(shift.jornadaId, data.userId, active.id);
            else await iniciarPausaJornada(shift.jornadaId, data.userId, tipo);
            setMessage(active ? 'Fin de pausa guardado' : 'Inicio de pausa guardado');
        } catch (error) { setMessage(error.message || 'No se pudo guardar la pausa. Reintentá.'); }
        finally { setBusy(false); }
    };
    return <Box sx={{ mt: 1.25 }} aria-busy={busy}>
        <Typography fontWeight={600} sx={{ fontSize: '0.82rem' }}>Pausas de la jornada</Typography>
        {message && <Alert severity="info" role="status" sx={{ my: 0.75, py: 0.25 }}>{message}</Alert>}
        {active ? <Stack spacing={0.75} sx={{ mt: 0.75 }}>
            <Chip size="small" sx={{ alignSelf: 'flex-start' }} label={`${types.find((t) => t.id === active.tipo_id)?.nombre || 'Pausa'} en curso · ${Math.max(0, Math.floor((now - Date.parse(active.inicio)) / 60000))} min`} />
            <Button variant="outlined" disabled={busy || !canEnd} onClick={() => run()} sx={{ minHeight: 40, borderRadius: '8px', fontSize: '0.82rem' }}>Volver al trabajo</Button>
        </Stack> : <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.75 }}>
            {types.map((t) => <Button key={t.id} variant="outlined" disabled={!canStart || busy || !data?.row || Boolean(data.row.fin)} onClick={() => run(t)} sx={{ minHeight: 40, borderRadius: '8px', fontSize: '0.82rem' }}>{t.codigo === 'almuerzo' ? 'Iniciar almuerzo' : t.codigo === 'descanso' ? 'Iniciar descanso' : `Iniciar ${t.nombre.toLocaleLowerCase('es')}`}</Button>)}
        </Stack>}
        {!types.length && !active && <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.74rem' }}>Coordinación debe configurar qué pausas se registran y cuáles cuentan como tiempo trabajado.</Typography>}
        {pauses.filter((p) => p.fin).map((p) => <Typography key={p.id} variant="body2" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
            {types.find((t) => t.id === p.tipo_id)?.nombre || 'Pausa'}: {Math.max(0, Math.floor((Date.parse(p.fin) - Date.parse(p.inicio)) / 60000))} min{p.cierre_automatico ? ' · Cerrada al finalizar la jornada' : ''}
        </Typography>)}
    </Box>;
}
